import {
  FormControl,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";

interface ToggleSelectorProps<T extends string> {
  title?: string;
  selectedValue: T | null;
  setSelectedValue: (value: T) => void;
  availableValueLabels: Record<T, string>;
  size?: "small" | "medium";
}

export const ToggleSelector = <T extends string>({
  title,
  selectedValue,
  setSelectedValue,
  availableValueLabels,
  size = "small",
}: ToggleSelectorProps<T>) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const onChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newValue: T) => {
      if (newValue) {
        setSelectedValue(newValue);
      }
    },
    [setSelectedValue]
  );
  return (
    <FormControl sx={{ gap: 0.5, alignSelf: "flex-end" }}>
      {title ? <FormLabel>{tenantT(title)}</FormLabel> : null}
      <ToggleButtonGroup
        color="primary"
        size={size}
        value={selectedValue}
        exclusive
        onChange={onChange}
      >
        {typeSafeObjectEntries(availableValueLabels).map(([value, label]) => (
          <ToggleButton
            key={value}
            value={value}
            sx={{ px: 1.5, height: 40, borderColor: "grey.100" }}
          >
            {t(label)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
};
